import type { ApiRuleVersion } from "@/api/perkpoint/rules";

export type EarnIssuance = "FixedPointsPerTxn" | "%OfInvoiceAmount" | "PointsPerSpend" | "Multiplier";
export type PointsRelease = "immediate" | "checkout";
export type ProductFilter = "any" | "hotel" | "flight" | "package";

export type EventEarnFields = {
  pointsIssuanceType: EarnIssuance;
  minInvoiceAmount: number;
  awardPoints: number;
  triggerEvent: string;
  release: PointsRelease;
  releaseDays: number;
  productType: ProductFilter;
  tierCredits: string[];
};

type JsonRecord = Record<string, unknown>;

const CHECKOUT_FIELD = "event.attributes.check_out_date";

export const PRODUCT_OPTIONS: { value: ProductFilter; label: string }[] = [
  { value: "any", label: "Any booking" },
  { value: "hotel", label: "Hotels" },
  { value: "flight", label: "Flights" },
  { value: "package", label: "Packages" },
];

export const EVENT_LABELS: Record<string, string> = {
  "booking.completed": "Booking completed",
  "booking.cancelled": "Booking cancelled",
  "payment.captured": "Payment captured",
  "member.enrolled": "Member enrolled",
};

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === "object" ? (value as JsonRecord) : null;
}

function conditionItems(conditions: unknown): JsonRecord[] {
  const record = asRecord(conditions);
  if (!record) return [];
  if (Array.isArray(record.items)) return record.items.filter((item) => asRecord(item)) as JsonRecord[];
  return record.field ? [record] : [];
}

function productOf(conditions: unknown, earn: JsonRecord | null): ProductFilter {
  const fromEarn = earn?.product_type;
  const raw = typeof fromEarn === "string"
    ? fromEarn
    : conditionItems(conditions).find(
        (item) => item.field === "event.attributes.product_type" && item.operator === "eq",
      )?.value;
  if (raw === "hotel" || raw === "flight" || raw === "package") return raw;
  return "any";
}

/** Reads a stored earn rule, including ones saved only as engine actions. */
export function readEventEarn(version: ApiRuleVersion | null | undefined): EventEarnFields | null {
  if (!version) return null;
  const earn = asRecord(version.earn);
  const actions = Array.isArray(version.actions) ? version.actions.map(asRecord).filter(Boolean) as JsonRecord[] : [];
  if (!earn && actions.length === 0) return null;

  const rate = actions.find((action) => action.type === "award_rate");
  const fixed = actions.find((action) => action.type === "award_fixed");
  const multiply = actions.find((action) => action.type === "multiply_points");
  const lock = actions.find((action) => action.type === "set_points_lock");
  const issuanceType = typeof earn?.issuance_type === "string" ? earn.issuance_type : "";

  let pointsIssuanceType: EarnIssuance = "FixedPointsPerTxn";
  let awardPoints = Number(earn?.award_points ?? 0);
  if (issuanceType === "points_per_spend") pointsIssuanceType = "PointsPerSpend";
  else if (issuanceType === "multiplier") pointsIssuanceType = "Multiplier";
  else if (issuanceType === "percent_of_invoice") pointsIssuanceType = "%OfInvoiceAmount";
  else if (issuanceType === "fixed_per_txn") pointsIssuanceType = "FixedPointsPerTxn";
  else if (rate && (rate.unit === "currency_100" || rate.unit == null || (rate.unit === "currency_1" && Number(rate.points_per_unit) > 1))) {
    pointsIssuanceType = "PointsPerSpend";
    awardPoints = Number(rate.points_per_unit);
  } else if (rate && rate.unit === "currency_1") {
    pointsIssuanceType = "%OfInvoiceAmount";
    awardPoints = Number(rate.points_per_unit) * 100;
  } else if (fixed) {
    pointsIssuanceType = "FixedPointsPerTxn";
    awardPoints = Number(fixed.points);
  } else if (multiply) {
    pointsIssuanceType = "Multiplier";
    awardPoints = Number(multiply.multiplier);
  } else if (!earn) {
    return null;
  }

  const releaseOn = typeof earn?.release_on === "string" ? earn.release_on : lock?.release_on;
  const checkout = typeof releaseOn === "string" && releaseOn.includes("check_out");
  const releaseDays = Number(earn?.release_offset_days ?? lock?.offset_days ?? 1);
  const trigger = typeof earn?.trigger_event_type === "string"
    ? earn.trigger_event_type
    : version.trigger_event_type_key || "payment.captured";

  return {
    pointsIssuanceType,
    minInvoiceAmount: Number(earn?.min_invoice_amount ?? 0),
    awardPoints,
    triggerEvent: trigger,
    release: checkout ? "checkout" : "immediate",
    releaseDays: Number.isFinite(releaseDays) ? releaseDays : 1,
    productType: productOf(version.conditions, earn),
    tierCredits: actions
      .filter((action) => action.type === "add_tier_credit" && typeof action.metric === "string")
      .map((action) => String(action.metric)),
  };
}

export function nativeEarnPayload(fields: {
  pointsIssuanceType: string;
  awardPoints: string;
  minInvoiceAmount: string;
  triggerEvent: string;
  release: string;
  releaseDays: string;
  productType: string;
}) {
  const issuance = fields.pointsIssuanceType === "%OfInvoiceAmount"
    ? "percent_of_invoice"
    : fields.pointsIssuanceType === "PointsPerSpend"
      ? "points_per_spend"
      : fields.pointsIssuanceType === "Multiplier"
        ? "multiplier"
        : "fixed_per_txn";
  return {
    issuance_type: issuance,
    award_points: Number(fields.awardPoints),
    min_invoice_amount: Number(fields.minInvoiceAmount || 0),
    trigger_event_type: fields.triggerEvent,
    release_on: fields.release === "checkout" ? CHECKOUT_FIELD : null,
    release_offset_days: fields.release === "checkout" ? Number(fields.releaseDays || 1) : null,
    product_type: fields.productType !== "any" ? fields.productType : null,
  };
}

export function releaseLabel(release: PointsRelease, days: number) {
  if (release !== "checkout") return "As soon as the event is processed";
  if (days === 1) return "The day after checkout";
  if (days === 0) return "On checkout";
  return `${days} days after checkout`;
}
